import { registerLocaleData } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import localeDeDe from '@angular/common/locales/de';
import { KFM_WEB_PRINT_SETTINGS } from '@shared/constants/constants';
import { IPrintSettings } from './print-settings.interface';
import { PrintSettings } from './print-settings.model';
import { KfmAppShoppingCartColumns } from '@shared/enums/shopping-cart-columns.enum';
import { LocalStorageService } from '@shared/storage/local-storage.service';
import { HsBlazorService } from '@hsetu/core-services';

registerLocaleData(localeDeDe);

@Injectable({
  providedIn: 'root',
})
export class KaufmannPrintSettingsService {
  private _settings!: IPrintSettings;

  private readonly _settingsKey = KFM_WEB_PRINT_SETTINGS;
  private readonly _storageService = inject(LocalStorageService);
  private readonly _serviceKey = 'kfmPrintSetting';
  private readonly _blazorService = inject(HsBlazorService);

  public get printSettingsOffer(): PrintSettings {
    return this._settings
      ? this._settings.printSettingsOffer
      : this.getDefaultPrintSettings();
  }

  public set printSettingsOffer(val: PrintSettings) {
    if (this._settings) {
      this._settings.printSettingsOffer = val;
    }
  }

  public get printSettingsOrder(): PrintSettings {
    return this._settings
      ? this._settings.printSettingsOrder
      : this.getDefaultPrintSettings();
  }

  public set printSettingsOrder(val: PrintSettings) {
    if (this._settings) {
      this._settings.printSettingsOrder = val;
    }
  }

  public get printSettingsInvoice(): PrintSettings {
    return this._settings
      ? this._settings.printSettingsInvoice
      : this.getDefaultPrintSettings();
  }

  public set printSettingsInvoice(val: PrintSettings) {
    if (this._settings) {
      this._settings.printSettingsInvoice = val;
    }
  }

  constructor() {
    this.getSettings();
  }

  public async getSettings(): Promise<IPrintSettings> {
    const settings: string | undefined = await this._storageService.get(
      this._settingsKey,
    );

    if (settings === undefined || settings.trim() === '') {
      this._settings = {
        printSettingsOffer: this.getDefaultPrintSettings(),
        printSettingsOrder: this.getDefaultPrintSettings(),
        printSettingsInvoice: this.getDefaultPrintSettings(),
      };

      await this._storageService.set(
        this._settingsKey,
        JSON.stringify(this._settings),
      );
    } else {
      const settingsObj = JSON.parse(settings);
      this._settings = {
        printSettingsOffer: this.mapPrintSettings(
          settingsObj['printSettingsOffer'],
        ),
        printSettingsOrder: this.mapPrintSettings(
          settingsObj['printSettingsOrder'],
        ),
        printSettingsInvoice: this.mapPrintSettings(
          settingsObj['printSettingsInvoice'],
        ),
      };
    }

    return this._settings;
  }

  public async saveSettingsToStorage() {
    try {
      const settings = {
        printSettingsOffer: this.unmapPrintSettings(
          this._settings.printSettingsOffer,
        ),
        printSettingsOrder: this.unmapPrintSettings(
          this._settings.printSettingsOrder,
        ),
        printSettingsInvoice: this.unmapPrintSettings(
          this._settings.printSettingsInvoice,
        ),
      };
      await this._storageService.set(
        this._settingsKey,
        JSON.stringify(settings),
      );
    } catch (reason: unknown) {
      console.log(reason);
    }
  }

  public async saveSettingsToDatabase(
    itemId: string,
    printSettings: PrintSettings,
  ) {
    const printSettingsResult = await this._blazorService.invokeService(
      this._serviceKey,
      'Create',
      {
        itemId: itemId,
        printSettings: printSettings,
      },
    );

    if (printSettingsResult) {
      const result = printSettingsResult as Record<
        string,
        string | undefined
      >['meta'];
      return result;
    }

    return undefined;
  }

  public unmapPrintSettings(settings: PrintSettings): unknown {
    return {
      PrintSenderAddress: settings.printSenderAddress,
      PrintSenderAddressLine: settings.printSenderAddressLine,
      PrintClientLogo: settings.printClientLogo,
      PrintCustomerAddress: settings.printCustomerAddress,
      PrintPropertyAddress: settings.printPropertyAddress,
      PrintPreText: settings.printPreText,
      PrintPostText: settings.printPostText,
      DisplayShoppingCartColumns: settings.displayShoppingCartColumns, // enum values are numbers
    };
  }

  public mapPrintSettings(json: unknown): PrintSettings {
    return {
      printSenderAddress:
        json['PrintSenderAddress'] ?? json['printSenderAddress'],
      printSenderAddressLine:
        json['PrintSenderAddressLine'] ?? json['printSenderAddressLine'],
      printClientLogo: json['PrintClientLogo'] ?? json['printClientLogo'],
      printCustomerAddress:
        json['PrintCustomerAddress'] ?? json['printCustomerAddress'],
      printPropertyAddress:
        json['PrintPropertyAddress'] ?? json['printPropertyAddress'],
      printPreText: json['PrintPreText'] ?? json['printPreText'],
      printPostText: json['PrintPostText'] ?? json['printPostText'],
      displayShoppingCartColumns:
        json['DisplayShoppingCartColumns'] ??
        json['displayShoppingCartColumns'],
    };
  }

  private getDefaultPrintSettings(): PrintSettings {
    return this.mapPrintSettings({
      printSenderAddress: true,
      printSenderAddressLine: true,
      printClientLogo: true,
      printCustomerAddress: true,
      printPropertyAddress: true,
      printPreText: true,
      printPostText: true,
      displayShoppingCartColumns: [
        KfmAppShoppingCartColumns.PositionNumber,
        KfmAppShoppingCartColumns.Quantity,
        KfmAppShoppingCartColumns.UnitOfMeasure,
        KfmAppShoppingCartColumns.ItemNumber,
        KfmAppShoppingCartColumns.Performance,
        KfmAppShoppingCartColumns.Vat,
        KfmAppShoppingCartColumns.UnitPrice,
        KfmAppShoppingCartColumns.TotalPrice,
      ],
    });
  }
}
